import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import io from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { OPEN_LOAN_EVENT } from '../utils/common.interface';
import { LoggerService } from './logger.service';
import { UserStateService } from './user-state.service';
import { WalletStateService } from './wallet-state.service';
import type { AllowanceSpender } from './allowance.service';

export interface UserDeletedEventPayload {
  userId: string | null;
}

/** Sent to the user only, when the RealWorld team adds, moves or removes them from a community. */
export interface UserCommunityUpdatedPayload {
  /** The new community's number, or null when they were removed. */
  community_id: number | null;
  community_name: string | null;
  previous_community_id: number | null;
  previous_community_name: string | null;
}

export interface SyndicateUpdatedPayload {
  loanId: string;
  syndicateId: string;
  initiatedUserId?: string;
}

export interface AllowanceDecreaseRequiredPayload {
  counterId: string;
  // Empty for the on-connect "pending-decrease sweep" — no specific loan to
  // exclude; we decrease against the full active counter sum.
  loanRequestId: string;
  currencyId: string;
  currencyAddress: string;
  currencySymbol?: string;
  allowanceAmount: number;
  /** Contract the excess is approved to; absent means the borrow-lend contract. */
  spender?: AllowanceSpender;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService {

  private readonly userDeletedEventName = 'USER_DELETED';
  private readonly allowanceDecreaseEventName = 'ALLOWANCE_DECREASE_REQUIRED';
  private readonly sellOrderCreatedEventName = 'SELL_ORDER_CREATED';
  private readonly sellOrderCancelledEventName = 'SELL_ORDER_CANCELLED';
  private readonly activityLogEventName = 'LOGS';
  private socket: any;
  private socketUserId: string | null = null;
  public notification$: Subject<string> = new Subject();
  public kycStatus$: Subject<any> = new Subject<any>();
  public marketPrice$: BehaviorSubject<number> = new BehaviorSubject(0);
  private deliveryStatus$: BehaviorSubject<{ [key: string]: any }[]> = new BehaviorSubject([{}]);
  private currentDeliveryStatus$: BehaviorSubject<{ [key: string]: any }> = new BehaviorSubject({});
  private depositStatus$: Subject<{ [key: string]: any }> = new Subject();
  private readonly userDeletedSubject = new Subject<UserDeletedEventPayload>();
  private readonly userCommunityUpdatedSubject = new Subject<UserCommunityUpdatedPayload>();
  private readonly allowanceDecreaseSubject = new Subject<AllowanceDecreaseRequiredPayload>();
  private readonly sellOrderCreatedSubject = new Subject<any>();
  private readonly sellOrderCancelledSubject = new Subject<any>();
  private readonly activityLogSubject = new Subject<any>();
  withdrawalStatus$ = new BehaviorSubject<any>(null);
  sellOrderStatus$ = new BehaviorSubject<any>(null);
  // Callbacks a mounted page registered via getLoanBasedEventData/getResellNegotiationData.
  // Replayed in initializeListeners() so a socket rebuilt on reconnect (e.g. header-new's
  // visibilitychange refocus handler) keeps delivering to the still-mounted page instead of
  // silently going quiet - see the note on listenNotification() for the same class of bug.
  private loanNegotiationCallback: ((data: { loanId: string, status: boolean }) => any) | null = null;
  private resellNegotiationCallback: ((data: { loan_sell_request_id: string }) => any) | null = null;

  constructor(
    private logger: LoggerService,
    private walletState: WalletStateService,
    private userState: UserStateService
  ) { 
  }

  connect() {
    const user = this.userState.user;
    const userId = user?._id ?? null;

    // Same user with a usable socket: reuse it. Deliberately not gated on `connected`
    // alone — connect() is called from both app.component and the header on every
    // user$ emission, and during the handshake window `connected` is still false, so
    // that guard tore down a socket that was seconds old and rebuilt it, silently
    // dropping every listener attached to the old instance. `active` covers the
    // handshake and any automatic reconnect; only a socket the client will not bring
    // back on its own (a server-side disconnect) is worth replacing.
    if (this.socket && this.socketUserId === userId && (this.socket.connected || this.socket.active)) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    const regulated = JSON.stringify(this.walletState.getRegulated());
    this.socketUserId = userId;
    this.socket = io(environment.SOCKET_BASE_URL, {
      withCredentials: true,
      query: {
        regulated,
        ...(userId ? { id: userId } : {})
      }
    });

    // attach listeners ONLY after socket exists
    this.initializeListeners();
  }

  /**
   * Attach listeners to socket events after socket connection is established.
   * This function is called after socket connection is established.
   * It will attach listeners to events such as sell order updates.
   */
  private initializeListeners() {
    if (!this.socket) return;
  
    this.logger.debug('Socket connected and listeners attached');
  
    this.listenNotification();
    this.listenUserDeleted();
    this.listenSellOrderUpdate();
    this.listenSellOrderCreated();
    this.listenSellOrderCancelled();
    this.listenActivityLog();
    this.listenAllowanceDecreaseRequired();
    this.listenKycStatusUpdate();
    this.listenDepositStatusUpdate();
    this.attachLoanLifecycleListeners();
    this.listenSyndicateUpdated();
    this.listenWithdrawalStatusUpdate();
    this.listenUserCommunityUpdated();

    // GOLD_VALUE_UPDATED has no per-page callback (it only ever feeds marketPrice$), so it
    // can attach unconditionally here, same as the other singleton listeners above.
    this.socket.on('GOLD_VALUE_UPDATED', ({ gold_value }: any) => {
      this.marketPrice$.next(Number(gold_value));
    });

    // LOAN_NEGOTIATION_UPDATE / LOAN_SELL_NEGOTIATION_UPDATE take a page-supplied callback,
    // so they can only be re-attached here if a page actually registered one.
    if (this.loanNegotiationCallback) {
      const callBack = this.loanNegotiationCallback;
      this.socket.on('LOAN_NEGOTIATION_UPDATE', (result: any) => {
        callBack({ loanId: result.loanId, status: true });
      });
    }
    if (this.resellNegotiationCallback) {
      const callBack = this.resellNegotiationCallback;
      this.socket.on('LOAN_SELL_NEGOTIATION_UPDATE', (result: any) => {
        this.logger.debug('Received resale negotiation socket event');
        callBack({ loan_sell_request_id: result });
      });
    }
  }
  
  

  disconnect() {
    if (this.socket) {
      // unsubscribe to events
      this.unsubscribeEvents();
      // disconnect socket
      this.socket.disconnect();
      this.socket = null;
      this.socketUserId = null;
    }
  }

  unsubscribeEvents() {
    if (this.socket) {
      this.socket.off(this.userDeletedEventName);
      this.socket.off(this.allowanceDecreaseEventName);
      // NOTE: do NOT off SELL_ORDER_CREATED here. unsubscribeEvents is called
      // from component ngOnDestroys (dashboard, nfts-listing, nft-detail),
      // so off-ing the singleton socket listener kills cross-tab refresh for
      // every other consumer. The listener attaches once in initializeListeners
      // and only needs to die when the socket disconnects.
      // unsubscribe to gold value update event and clear its value
      this.socket.off('GOLD_VALUE_UPDATED')
      this.marketPrice$.next(0);
    }
  }

  getUserDeletedEvent(): Observable<UserDeletedEventPayload> {
    return this.userDeletedSubject.asObservable();
  }

  getUserCommunityUpdatedEvent(): Observable<UserCommunityUpdatedPayload> {
    return this.userCommunityUpdatedSubject.asObservable();
  }

  getAllowanceDecreaseRequiredEvent(): Observable<AllowanceDecreaseRequiredPayload> {
    return this.allowanceDecreaseSubject.asObservable();
  }

  triggerAllowanceDecrease(payload: AllowanceDecreaseRequiredPayload): void {
    this.allowanceDecreaseSubject.next(payload);
  }

  // Attaches once in initializeListeners() (singleton). Pushing to the shared
  // kycStatus$ lets the header AND the KYC pages all react to one socket
  // listener, instead of every getKycStatusUpdate() call re-registering a
  // handler. No off() in unsubscribeEvents — kills cross-tab refresh otherwise.
  private listenKycStatusUpdate(): void {
    this.socket?.on('USER_KYC_STATUS_UPDATED', (status: any) => {
      this.kycStatus$.next(status);
    });
  }

  getKycStatusUpdate() {
    return this.kycStatus$.asObservable().pipe(distinctUntilChanged());
  }

  // Attaches once in initializeListeners() (singleton), like the KYC / withdrawal /
  // deposit listeners. It used to attach lazily inside getNotification(), which bound
  // the handler to whichever socket existed at that moment: a socket rebuild in
  // connect(), or a login that never re-ran the caller's ngOnInit, left the app with
  // no NOTIFICATION handler at all — the bell list still filled in over HTTP, but the
  // push banner never fired again.
  private listenNotification(): void {
    this.socket?.on('NOTIFICATION', (notification: any) => {
      this.notification$.next(notification);
    });
  }

  getNotification(): Observable<any> {
    return this.notification$.asObservable();
  }

  /**
   * Gets loan notification
   * @param callBack 
   */
  getLoanNotification(callBack: (res: any) => any): Subscription {
    return this.notification$.subscribe((notification: any) => {
      notification ? callBack(notification) : notification
    });
  }

  getDeliveryStatus() {
    this.socket?.on('DELIVERY_STATUS_UPDATE', (status: any) => {
      this.deliveryStatus$.next(status);
    })

    return this.deliveryStatus$.asObservable();
  }

  /**
   * get current delivery status of the nft
   */
  getCurrentDeliveryStatus() {
    this.socket?.on('NFT_DELIVERY_STATUS', (status: any) => {
      this.currentDeliveryStatus$.next(status);
    })

    return this.currentDeliveryStatus$.asObservable();
  }

  /**
   * Deposit lifecycle updates pushed when ops moves an asset through the custody pipeline
   * from the management dApp. The payload carries the deposit id plus its new status,
   * history and tracking id, so the Deposits page can patch the record in place.
   *
   * A plain Subject rather than a BehaviorSubject: there is no meaningful "current" deposit
   * status across the whole list, only discrete change events.
   */
  private listenDepositStatusUpdate(): void {
    this.socket?.on('DEPOSIT_STATUS_UPDATED', (payload: any) => {
      this.depositStatus$.next(payload);
    });
  }

  getDepositStatusUpdates() {
    return this.depositStatus$.asObservable();
  }

  // GOLD_VALUE_UPDATED itself is attached once in initializeListeners() (no per-page
  // callback to lose track of), so this just exposes the stream.
  getMarketPrice() {
    return this.marketPrice$.asObservable();
  }

  /**
   * Gets loan based event data
   * @param [isListener]
   * based on listerner off the event listener.
   * @param callBack
   */
  getLoanBasedEventData(isListener: boolean = false, callBack: (notificationdata: { loanId: string, status: boolean }) => any) {
    if (isListener) {
      this.loanNegotiationCallback = callBack;
      this.socket?.on('LOAN_NEGOTIATION_UPDATE', (result: any) => {
        let emitData = { loanId: result.loanId, status: true }
        callBack(emitData)
      })
    } else {
      this.loanNegotiationCallback = null;
      this._handleListernerOff()
    }
  }

  /**
   * Handles listerner off
   */
  _handleListernerOff() {
    this.socket?.off('LOAN_NEGOTIATION_UPDATE')
  }

  getResellNegotiationData(isListener: boolean = false, callBack: (notificationdata: { loan_sell_request_id: string }) => any) {
    if (isListener) {
      this.resellNegotiationCallback = callBack;
      this.socket?.on('LOAN_SELL_NEGOTIATION_UPDATE', (result: any) => {
        this.logger.debug('Received resale negotiation socket event');
        let emitData = { loan_sell_request_id: result }
        callBack(emitData)
      })
    } else {
      this.resellNegotiationCallback = null;
      this._handleResellListernerOff()
    }
  }

  /**
   * Handles listerner off
   */
    _handleResellListernerOff() {
      this.socket?.off('LOAN_SELL_NEGOTIATION_UPDATE')
    }

  // Loan lifecycle events emitted by the backend on the matching API calls.
  // Listen-only: open listing/detail pages subscribe and re-fetch so other
  // tabs / the counterparty see create / update / cancel changes (and resale
  // request creation) in real time without a manual refresh.
  private readonly loanLifecycleEventNames = [
    'LOAN_REQUESTED',
    'REFINANCE_REQUESTED',
    'LOAN_REQUEST_CANCELLED',
    'REFINANCE_REQUEST_CANCELLED',
    'LOAN_SELL_REQUEST_CREATED',
    'LOAN_UPDATED',
    'REFINANCE_REQUEST_UPDATED'
  ];

  // Single fan-out for every loan lifecycle event. The raw socket handlers are
  // attached once per socket in initializeListeners() (so they survive a
  // reconnect that rebuilds the socket) and only die with the socket; consumers
  // subscribe/unsubscribe here instead of off-ing the shared socket listeners.
  private readonly loanLifecycleSubject = new Subject<{ event: string, payload: any }>();
  readonly loanLifecycleEvent$ = this.loanLifecycleSubject.asObservable();

  /**
   * Attaches the raw loan lifecycle socket handlers exactly once per socket.
   * Called from initializeListeners() so the listeners are re-established
   * whenever connect() rebuilds the socket (e.g. after a reconnect).
   */
  private attachLoanLifecycleListeners() {
    this.loanLifecycleEventNames.forEach((eventName) => {
      this.socket?.on(eventName, (payload: any) => {
        this.logger.debug(`Received ${eventName} socket event`, payload);
        this.loanLifecycleSubject.next({ event: eventName, payload });
      });
    });
  }

  /**
   * Subscribes to loan lifecycle events. Returns the RxJS Subscription so the
   * caller can unsubscribe in ngOnDestroy; the shared socket handlers are NOT
   * removed here (off-ing them would kill every other active consumer).
   * @param [isListener] subscribe (true) or no-op (false, kept for back-compat)
   * @param callBack invoked with the event name and its raw payload
   */
  getLoanLifecycleEvents(isListener: boolean = false, callBack: (data: { event: string, payload: any }) => any): Subscription | void {
    if (!isListener) return;
    return this.loanLifecycleEvent$.subscribe(callBack);
  }

  /**
   * Gets open loan event data
   * @param [isListener] 
   * @param callBack 
   */
  getOpenLoanEventData(isListener: boolean = false, callBack: (notificationdata: { loanId: string, status: boolean }) => any) {
    if (isListener) {
      this.socket?.on(OPEN_LOAN_EVENT, (result: any) => {        
        let emitData = { loanId: result.loanOfferId, status: true }
        callBack(emitData)
      })
    } else {
      this._handleOpenLoanListernerOff()
    }
  }

 
  /**
   * Handles open loan listerner off
   */
  _handleOpenLoanListernerOff() {
    this.socket?.off(OPEN_LOAN_EVENT)
  }

  /**
   * Gets the withdrawal status update
   * @returns {Observable<any>} - observable that emits the updated withdrawal status
   * 
   * This function listens to the 'UPDATE_WITHDRAWAL_STATUS' event and updates the withdrawal status
   * It returns an observable that emits the updated withdrawal status and is distinct until changed
   */
  getWithdrawalStatusUpdate() {
    // Pure accessor. The socket listener is attached once in initializeListeners()
    // via listenWithdrawalStatusUpdate(), so approve/reject events reach the shared
    // withdrawalStatus$ even for users who already have an approved address (the
    // change-address flow) and regardless of whether this accessor was ever called.
    return this.withdrawalStatus$.asObservable().pipe(distinctUntilChanged());
  }

  // Attaches once in initializeListeners() (singleton). Registering here rather than
  // lazily inside getWithdrawalStatusUpdate() ensures the change-address flow (user
  // already has an approved address) still receives UPDATE_WITHDRAWAL_STATUS events.
  private listenWithdrawalStatusUpdate(): void {
    this.socket?.on('UPDATE_WITHDRAWAL_STATUS', (response: any) => {
      this.withdrawalStatus$.next(response);
    });
  }

  // Attaches once in initializeListeners() (singleton), like the KYC / withdrawal listeners.
  // app.component reacts to it (CommunityMembershipService), whatever page is open.
  private listenUserCommunityUpdated(): void {
    this.socket?.on('USER_COMMUNITY_UPDATED', (payload: UserCommunityUpdatedPayload | null) => {
      if (payload) this.userCommunityUpdatedSubject.next(payload);
    });
  }

  /**
   * Listens to the 'NFT_SELL_ORDER_UPDATED' event and updates the sell order status
   * 
   * This function listens to the 'NFT_SELL_ORDER_UPDATED' event and updates the sell order status
   * It logs the received response and emits the updated sell order status
   */
  private listenSellOrderUpdate() {
    this.socket.on('NFT_SELL_ORDER_UPDATED', (response: any) => {
      this.sellOrderStatus$.next(response);
    });
  }

  // Fires when the backend successfully records a new sell order. Used by
  // open NFT tabs to refetch state and pick up the listed/locked status that
  // was created in another tab — without a manual refresh.
  private listenSellOrderCreated() {
    this.socket.on(this.sellOrderCreatedEventName, (payload: any) => {
      this.logger.debug('Received SELL_ORDER_CREATED socket event', payload);
      this.sellOrderCreatedSubject.next(payload);
    });
  }

  getSellOrderCreatedEvent(): Observable<any> {
    return this.sellOrderCreatedSubject.asObservable();
  }

  // Counterpart to SELL_ORDER_CREATED: fires when a sell order is cancelled
  // so open tabs can refetch and drop the cancel-sale UI back to the listing
  // form without a manual refresh.
  private listenSellOrderCancelled() {
    this.socket.on(this.sellOrderCancelledEventName, (payload: any) => {
      this.logger.debug('Received SELL_ORDER_CANCELLED socket event', payload);
      this.sellOrderCancelledSubject.next(payload);
    });
  }

  getSellOrderCancelledEvent(): Observable<any> {
    return this.sellOrderCancelledSubject.asObservable();
  }

  // LOGS is broadcast to every client whenever the backend writes an activity log
  // (create-log-controller), as { custodial, message: { type, nft_ids, ... } }. Pages filter it:
  // NFT details reloads its bids / loan offers when a log is an offer on the open asset.
  private listenActivityLog() {
    this.socket.on(this.activityLogEventName, (payload: any) => {
      this.activityLogSubject.next(payload);
    });
  }

  getActivityLogEvent(): Observable<any> {
    return this.activityLogSubject.asObservable();
  }

  private listenUserDeleted() {
    this.socket.on(this.userDeletedEventName, (payload: { userId?: unknown } | null) => {
      const userId = this.normalizeUserId(payload?.userId);
      this.logger.debug('Received USER_DELETED socket event', { userId });
      this.userDeletedSubject.next({ userId });
    });
  }

  private listenAllowanceDecreaseRequired() {
    this.socket.on(this.allowanceDecreaseEventName, (payload: Partial<AllowanceDecreaseRequiredPayload> | null) => {
      // loanRequestId is allowed to be empty for the on-connect sweep.
      if (!payload?.counterId || !payload?.currencyId || !payload?.currencyAddress) {
        return;
      }
      this.logger.debug('Received ALLOWANCE_DECREASE_REQUIRED socket event', payload);
      this.allowanceDecreaseSubject.next({
        counterId: String(payload.counterId),
        loanRequestId: payload.loanRequestId ? String(payload.loanRequestId) : '',
        currencyId: String(payload.currencyId),
        currencyAddress: String(payload.currencyAddress),
        currencySymbol: payload.currencySymbol ? String(payload.currencySymbol) : undefined,
        allowanceAmount: Number(payload.allowanceAmount ?? 0),
        spender: payload.spender === 'exchange' ? 'exchange' : 'lending'
      });
    });
  }

  /**
   * Returns an observable that emits the updated sell order status
   *
   * The observable will emit the updated sell order status whenever the 'NFT_SELL_ORDER_UPDATED' event is received
   * The observable is distinct until changed, meaning that only unique values will be emitted
   *
   * @returns {Observable<any>} - observable that emits the updated sell order status
   */
  getSellOrderUpdate() {
    return this.sellOrderStatus$.asObservable().pipe(distinctUntilChanged());
  }

  /**
   * Subscribes to syndicate fund updated events. Returns the RxJS Subscription so
   * the caller can unsubscribe in ngOnDestroy; the shared socket handler is NOT
   * removed here (off-ing it would kill every other active consumer).
   * @param [isListener] subscribe (true) or no-op (false, kept for back-compat)
   * @param callBack - callback function to handle the event data
   */
  getSyndicateFundUpdatedEvent(isListener: boolean = false, callBack: (data: SyndicateUpdatedPayload) => any): Subscription | void {
    if (!isListener) return;
    return this.syndicateUpdated$.subscribe(callBack);
  }

  // Single fan-out for SYNDICATE_UPDATED, mirroring the loan lifecycle events.
  // The raw handler is attached once per socket in initializeListeners() so it
  // survives connect() rebuilding the socket (login, logout, regulated-flag
  // change, server-side disconnect) and is present even when a consumer mounts
  // before the socket exists. Previously the component attached
  // `this.socket?.on(...)` itself from ngOnInit, which silently no-opped on a
  // hard refresh straight onto a loan detail URL (user$ had not resolved yet, so
  // there was no socket) and was lost for good whenever the socket was rebuilt.
  private readonly syndicateUpdatedSubject = new Subject<SyndicateUpdatedPayload>();
  readonly syndicateUpdated$ = this.syndicateUpdatedSubject.asObservable();

  /**
   * Attaches the raw SYNDICATE_UPDATED handler exactly once per socket.
   * Called from initializeListeners() so it is re-established whenever
   * connect() rebuilds the socket.
   */
  private listenSyndicateUpdated() {
    this.socket?.on('SYNDICATE_UPDATED', (result: any) => {
      this.logger.debug('Received SYNDICATE_UPDATED socket event', result);
      const loanId = this.normalizeId(result?.loanId);
      if (!loanId) return;
      this.syndicateUpdatedSubject.next({
        loanId,
        syndicateId: this.normalizeId(result?.syndicateId) ?? '',
        initiatedUserId: this.normalizeId(result?.initiatedUserId) ?? undefined
      });
    });
  }

  /**
   * Normalizes an id that may arrive as a bare id, an ObjectId, or a populated
   * document. Some emitters send `syndicate.loan_request_id` while the doc is
   * populated, in which case the payload field is the whole loan request object
   * and a plain `=== loanRequest._id` comparison on the consumer side never
   * matches.
   */
  private normalizeId(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const raw = typeof value === 'object' ? (value as any)._id : value;
    // An object with no usable _id is not an id — returning String(raw) here
    // would yield "[object Object]", which passes an emptiness check and then
    // silently fails to match anything.
    if (raw === null || raw === undefined || typeof raw === 'object') return null;
    const normalized = String(raw).trim();
    return normalized.length ? normalized : null;
  }

  private normalizeUserId(userId: unknown): string | null {
    if (userId === null || userId === undefined) {
      return null;
    }

    const normalizedUserId = String(userId).trim();
    return normalizedUserId.length ? normalizedUserId : null;
  }
}
